import { i18n } from '../i18n.js';
import { MoonfallCloud } from '../convex-client.js';
/**
 * One-time starter Stardust celebration. The server owns both the grant and
 * its durable pending receipt; this module consumes only after a real lobby is
 * visible, so invitation-created accounts can finish their match first.
 */

import { ArrivalCeremony } from './arrival-ceremony.js';
import { FX } from './fx.js';
import { InviteWelcome } from './invite-welcome.js';
import { Referrals } from './referrals.js';
import { RewardScene } from './reward-scene.js';
import { SoundEffects } from './sound-effects.js';
import { Stardust } from './stardust.js';
import { uiCssPx, uiPx } from './ui-scale.js';
let StarterReward = {};

StarterReward = (function () {
  const OVERLAY_ID = 'starter-reward-overlay';
  const REWARD_SOUND_LOAD_GRACE_MS = 900;
  const formatStardust = (value) => Stardust?.format?.(value) ?? String(value);
  let consumeInFlight = null;

  function uiText(key, fallback, replacements = {}) {
    const translated = i18n?.t?.(key, replacements);
    const template = translated && translated !== key ? translated : fallback;
      /* BOTH brace styles, double FIRST.
         The copy uses `{{name}}` (as ui.json writes it) and, in a few
         places, single `{name}`. A helper that knows only one leaves the
         other on screen whenever i18n has not resolved. Order matters:
         substituting the single form first turns `{{count}}` into `{3}`,
         which is exactly how starter-reward.js was rendering it. */
      return Object.entries(replacements || {}).reduce(
          (result, [name, value]) => String(result)
              .split(`{{${name}}}`).join(String(value))
              .split(`{${name}}`).join(String(value)),
          String(template),
      );
  }

  function lobbyIsVisible() {
    const lobby = document.getElementById('lobby-view');
    if (!lobby || lobby.hidden) return false;
    return window.getComputedStyle(lobby).display !== 'none'
      && window.getComputedStyle(lobby).visibility !== 'hidden';
  }

  function hasPendingInvite() {
    try {
      const roomId = new URLSearchParams(window.location.search).get('room');
      return typeof roomId === 'string' && roomId.length > 0;
    } catch (_) {
      return false;
    }
  }

  /* The mark and the spark field live in UI.RewardScene because the invitation
     brief is built from the same parts. Two copies of this look is exactly how
     the celebration screens drifted apart in the first place. */
  /* IMPORTED, not fetched off `window.UI`. The ESM conversion removed that
     global from the shipping bundle — it survives only in the bridged build the
     test suite uses — so these two reads threw on a real page, which killed the
     reward card, which killed the whole arrival ceremony behind it. The
     real-backend referral spec caught it as `#invite-welcome-overlay` never
     appearing for an invited account. */
  const createRewardMark = () => RewardScene.createMark();
  const createSparkField = () => RewardScene.createSparks();

  const PASS_TIERS = { moonfall_storyteller: 'store.storytellerPass', moonfall_manga: 'store.mangaPass' };
  const PASS_FALLBACKS = { moonfall_storyteller: 'Storyteller Pass', moonfall_manga: 'Manga Pass' };

  /* A REDEEMED VOUCHER may carry up to five grants; only the well-formed ones
     are shown, and a voucher that carries none opens nothing. */
  function validGrants(grants) {
    if (!Array.isArray(grants)) return [];
    return grants.filter((grant) => {
      if (!grant || typeof grant !== 'object') return false;
      switch (grant.kind) {
        case 'stardust': return Number.isSafeInteger(grant.amount) && grant.amount > 0;
        case 'card_packs': return Number.isSafeInteger(grant.count) && grant.count > 0;
        case 'moonlight': return Number.isSafeInteger(grant.usdMicros) && grant.usdMicros > 0;
        case 'complimentary_pass':
          return Object.prototype.hasOwnProperty.call(PASS_TIERS, grant.tier);
        case 'cardback': return typeof grant.slug === 'string' && grant.slug.length > 0;
        default: return false;
      }
    });
  }

  function passName(tier) {
    return uiText(PASS_TIERS[tier], PASS_FALLBACKS[tier] || 'Moon Pass');
  }

  /** One line per grant, in the server's order — the same sentences the
      voucher link's preview and the arrival ticket print. */
  function grantLine(grant, moonlightPercent) {
    switch (grant.kind) {
      case 'stardust':
        return uiText('voucher.grantStardust', '+{{amount}} Stardust', { amount: formatStardust(grant.amount) });
      case 'card_packs':
        return grant.count === 1
          ? uiText('voucher.grantPackOne', '+1 card pack')
          : uiText('voucher.grantPacks', '+{{count}} card packs', { count: grant.count });
      case 'moonlight':
        return Number.isFinite(moonlightPercent)
          ? uiText('referral.rewardMoonlightFull',
            'Moonlight — your moon is now {percent}% full; the reward stays until you use it',
            { percent: Math.max(0, Math.round(moonlightPercent)) })
          : uiText('voucher.grantMoonlightAmount', 'Extra Moonlight play time');
      case 'complimentary_pass':
        return grant.boostUsdMicros > 0
          ? uiText('voucher.grantPassBoost', '{{pass}} for one month, with extra Moonlight', { pass: passName(grant.tier) })
          : uiText('voucher.grantPass', '{{pass}} for one month', { pass: passName(grant.tier) });
      case 'cardback':
        return uiText('voucher.grantCardback', 'A new cardback');
      default:
        return '';
    }
  }

  function show(reward) {
    /* THREE REWARDS SHARE THIS SCREEN AND THEY ARE PAID IN DIFFERENT THINGS.
       The welcome gift is Stardust; a qualified referral is card packs plus
       Moonlight; a redeemed voucher is whatever its grants say. Gating on
       `amount` alone refused every referral payout after the reward changed
       shape, so the grant landed and nothing was ever shown. `amount` still
       opens the screen for rows written before that change. */
    const referralReward = reward?.kind === 'referral';
    const voucherReward = reward?.kind === 'voucher';
    const grants = voucherReward ? validGrants(reward.grants) : [];
    const grantOf = (kind) => grants.find((grant) => grant.kind === kind) || null;
    const figure = (value) => (Number.isSafeInteger(value) && value > 0 ? value : 0);
    const amount = voucherReward ? figure(grantOf('stardust')?.amount) : figure(reward?.amount);
    const packs = referralReward ? figure(reward?.packs) : (voucherReward ? figure(grantOf('card_packs')?.count) : 0);
    const moonlightUsdMicros = referralReward
      ? figure(reward?.moonlightUsdMicros)
      : (voucherReward ? figure(grantOf('moonlight')?.usdMicros) : 0);
    if (voucherReward && !grants.length) return false;
    if (!voucherReward && !amount && !packs && !moonlightUsdMicros) return false;
    if (document.getElementById(OVERLAY_ID)) return true;

    const previousFocus = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const app = document.getElementById('app');
    const appWasInert = Boolean(app?.inert);
    if (app) app.inert = true;

    const overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    /* A voucher can be redeemed with the Moon Store open beneath it, and the
       store sits at z-index 30000 — above this overlay's own 10020. The
       modifier lifts the voucher ceremony above the store; nothing else here
       changes. */
    overlay.className = voucherReward
      ? 'starter-reward-overlay starter-reward-overlay--voucher'
      : 'starter-reward-overlay';
    overlay.appendChild(createSparkField());

    const dialog = document.createElement('section');
    dialog.className = 'starter-reward-card';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'starter-reward-title');

    const eyebrow = document.createElement('p');
    eyebrow.className = 'starter-reward-eyebrow';
    eyebrow.textContent = voucherReward
      ? uiText('voucher.rewardEyebrow', 'CODE REDEEMED')
      : (referralReward
        ? uiText('referral.rewardEyebrow', 'INVITATION COMPLETE')
        : uiText('starterReward.eyebrow', 'WELCOME GIFT'));

    const title = document.createElement('h2');
    title.id = 'starter-reward-title';
    title.className = 'starter-reward-title';
    title.textContent = voucherReward
      ? uiText('voucher.rewardTitle', 'Your gift has arrived')
      : (referralReward
        ? uiText('referral.rewardTitle', 'The moonlight returned')
        : uiText('starterReward.title', 'The Moon chose you'));

    /* The headline figure is whatever this reward is actually denominated in:
       packs for a referral, Stardust for the welcome gift. A legacy referral
       row carrying only Stardust still reads in Stardust. */
    /* THE VOUCHER HEADLINE is the biggest thing in the code: Stardust counts
       up when there is any, else the moon's fullness, else the pass, else the
       packs, else the cardback. The rows below list every grant regardless. */
    const voucherPass = voucherReward ? grantOf('complimentary_pass') : null;
    const voucherPercent = Number(reward?.moonlightPercent);
    const voucherStaticHeadline = voucherReward && !amount
      ? (moonlightUsdMicros > 0 && Number.isFinite(voucherPercent)
        ? uiText('voucher.rewardMoonlight', '{{percent}}% full', { percent: Math.max(0, Math.round(voucherPercent)) })
        : (voucherPass
          ? uiText('voucher.grantPass', '{{pass}} for one month', { pass: passName(voucherPass.tier) })
          : (packs > 0
            ? uiText('referral.rewardAmount', '+{packs} packs', { packs })
            : (moonlightUsdMicros > 0
              ? uiText('voucher.grantMoonlightAmount', 'Extra Moonlight play time')
              : uiText('voucher.grantCardback', 'A new cardback')))))
      : null;
    const countsPacks = referralReward && packs > 0;
    const headlineValue = voucherStaticHeadline ? 0 : (countsPacks ? packs : amount);
    const amountText = (value) => (voucherStaticHeadline
      ? voucherStaticHeadline
      : (countsPacks
        ? uiText('referral.rewardAmount', '+{packs} packs', { packs: value })
        : uiText('starterReward.amount', '+{amount} Stardust', { amount: value })));
    const displayAmountText = (value) => amountText(
      countsPacks ? value : formatStardust(value),
    );
    const amountEl = document.createElement('div');
    amountEl.className = 'starter-reward-amount';
    amountEl.dataset.countValue = String(headlineValue);
    amountEl.setAttribute('aria-label', amountText(headlineValue));
    amountEl.textContent = displayAmountText(headlineValue);

    /* THE GRANT ROWS: what the code delivered, one line each, in the server's
       order — the same sentences the link preview and the arrival ticket use. */
    const grantsEl = voucherReward ? document.createElement('ul') : null;
    if (grantsEl) {
      grantsEl.className = 'starter-reward-grants';
      for (const grant of grants) {
        const row = document.createElement('li');
        row.className = 'starter-reward-grant';
        row.dataset.grantKind = grant.kind;
        row.textContent = grantLine(grant, moonlightUsdMicros > 0 ? voucherPercent : NaN);
        grantsEl.appendChild(row);
      }
    }

    /* MOONLIGHT IS PART OF THE PAYOUT, and how long it lasts is stated here
       rather than discovered later in a meter. Reward Moonlight is kept until
       it is spent on every plan (owner, 2026-10-05); a pass's own Moonlight
       resets weekly, so the line says which kind this is. */
    const moonlightEl = moonlightUsdMicros > 0 && !voucherReward
      ? document.createElement('p')
      : null;
    if (moonlightEl) {
      /* `starter-reward-subline` had NO CSS RULE ANYWHERE, so this rendered as
         unstyled body text beneath a 2.6rem gold headline. That is most of why
         the Moonlight half read as no reward at all. */
      moonlightEl.className = 'starter-reward-moonlight';
      /* SAY HOW FULL THE MOON IS — the product's one honest single number for
         Moonlight, and the same one the chip shows. Deliberately NOT a duel
         count: game/utils/moonlightMeter.js states a duel figure "is never a
         headline" because story, PvP and manga duels cost differently and one
         averaged number is "true of nothing".
         A missing figure falls back to the plain sentence rather than printing
         0%, which would read as "you got nothing" to someone whose status read
         merely failed. */
      const percent = Number(reward?.moonlightPercent);
      moonlightEl.textContent = Number.isFinite(percent)
        ? uiText('referral.rewardMoonlightFull',
          /* HOW LONG IT LASTS STAYS IN THE SENTENCE, in every language. The
             moon's percentage mixes this reward with a plan's weekly Moonlight,
             and only the reward outlives the next reset. */
          'Moonlight — your moon is now {percent}% full; the reward stays until you use it', {
          percent: Math.max(0, Math.round(percent)),
        })
        : uiText(
          'referral.rewardMoonlight',
          'Plus Moonlight play time, kept until you use it.',
        );
    }

    const cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'starter-reward-cta';
    /* NAME THE THING THE BUTTON DOES. "Claim in Lobby" was a promise with no
       handler behind it — the click only dismissed, leaving the player in the
       lobby with no idea where three card packs had gone. When the payout
       actually contains packs the button says so and goes there. */
    const opensPacks = countsPacks || (voucherReward && packs > 0);
    cta.textContent = opensPacks
      ? uiText('referral.rewardCtaPacks', 'Open your packs')
      : (voucherReward
        ? uiText('voucher.rewardCta', 'Continue')
        : (referralReward
          ? uiText('referral.rewardCta', 'Claim in Lobby')
          : uiText('starterReward.cta', 'Enter the Lobby')));
    cta.setAttribute(
      'aria-label',
      voucherReward
        ? uiText('voucher.rewardCloseLabel', 'Close reward')
        : (referralReward
          ? uiText('referral.rewardCloseLabel', 'Close referral reward')
          : uiText('starterReward.closeLabel', 'Enter the lobby and close reward')),
    );

    dialog.append(createRewardMark(), eyebrow, title, amountEl);
    if (grantsEl) dialog.appendChild(grantsEl);
    if (moonlightEl) dialog.appendChild(moonlightEl);
    dialog.appendChild(cta);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
    // Hold the ceremony's tail while this screen is on the frame, so the
    // Founder's Legacy banner cannot land on top of the Stardust grant.
    ArrivalCeremony.hold();

    if (headlineValue > 0) {
      FX?.animateCount?.(amountEl, 0, headlineValue, {
        duration: 900,
        render: displayAmountText,
      });
    }
    // The reveal remains immediate. On a brand-new login the Convex sound
    // manifest can finish just after the receipt arrives, so this cue alone
    // gets a bounded readiness grace; it is never replayed after the count-up.
    const cancelPendingRewardSound = SoundEffects?.play?.('card_reveal', {
      waitForLoadMs: REWARD_SOUND_LOAD_GRACE_MS,
    });

    let closed = false;
    function finalizeClose() {
      overlay.remove();
      if (app) app.inert = appWasInert;
      if (previousFocus?.isConnected) previousFocus.focus();
      const stardust = document.getElementById('user-stardust');
      /* The chip is `display: none` while the store is open, and a burst at a
         box with no geometry paints at the page origin. */
      if (stardust && (typeof stardust.getClientRects !== 'function' || stardust.getClientRects().length)) {
        FX?.burst?.(stardust, { count: 14, radiusX: uiPx(48), radiusY: uiPx(34) });
      }
      /* A voucher's caller owns its side effects (chip repaint, banner drain,
         the store re-render): it runs them now, before the ceremony's hold is
         released, so nothing queued behind the chain lands in between. */
      if (voucherReward && typeof reward?.onClose === 'function') {
        try { reward.onClose(); } catch (_) { /* the reward is already durable */ }
      }
      /* THE CEREMONY CONTINUES HERE, one screen at a time.
         A referral payout if one is waiting, and then the invitation brief.
         Sequencing them at the close of this screen — rather than firing them
         all at lobby entry, which is what used to happen — is the only thing
         that stops two celebration overlays from landing on the same frame.
         A voucher is not part of the arrival chain and hands off to nothing. */
      Promise.resolve((referralReward || voucherReward) ? false : Referrals?.consumeAndShowRewards?.())
        .then((shown) => (shown ? false : InviteWelcome?.checkAndShow?.()))
        .catch(() => {})
        // Released AFTER the handoff is dispatched: the next screen takes its
        // own hold inside the settle window, so the tail never fires into the
        // gap between them.
        .finally(() => ArrivalCeremony.release());
    }

    function dismiss() {
      if (closed) return;
      closed = true;
      if (typeof cancelPendingRewardSound === 'function') cancelPendingRewardSound();
      document.removeEventListener('keydown', onKeyDown, true);
      overlay.classList.add('starter-reward-overlay--closing');
      const reduce = FX?.reducedMotion?.() === true
        || window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
      if (!reduce && typeof dialog.animate === 'function') {
        try {
          const animation = dialog.animate([
            { transform: 'translateY(0) scale(1)', opacity: 1 },
            { transform: `translateY(${uiCssPx(10)}) scale(0.98)`, opacity: 0 },
          ], { duration: 180, easing: 'ease-in', fill: 'forwards' });
          animation.finished.then(finalizeClose, finalizeClose);
          return;
        } catch (_) { /* fall through to immediate cleanup */ }
      }
      finalizeClose();
    }

    function onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        dismiss();
        return;
      }
      if (event.key === 'Tab') {
        event.preventDefault();
        cta.focus();
      }
    }

    cta.addEventListener('click', () => {
      /* ASK, DO NOT IMPORT. `collection.js` already imports this module, so
         importing `Collection` back would be a cycle. `main.js` is the
         composition root, holds `Collection` statically, and already opens the
         Exchange section this way. Only when there are packs to look at — a
         Moonlight-only payout must not send the player to a screen with
         nothing new on it. */
      if (opensPacks) {
        try {
          window.dispatchEvent(new CustomEvent('moonfall:open-card-packs'));
        } catch (_) { /* the reward is durable; navigation is a courtesy */ }
      }
      dismiss();
    });
    document.addEventListener('keydown', onKeyDown, true);
    cta.focus();
    return true;
  }

  function consumeAndShow() {
    if (!lobbyIsVisible() || hasPendingInvite()) return Promise.resolve(false);
    if (document.getElementById(OVERLAY_ID)) return Promise.resolve(true);
    if (consumeInFlight) return consumeInFlight;
    const consume = MoonfallCloud?.users?.consumeStarterRewardAnnouncement;
    if (typeof consume !== 'function') return Promise.resolve(false);

    consumeInFlight = Promise.resolve()
      .then(() => consume.call(MoonfallCloud.users))
      .then((reward) => (reward ? show(reward) : false))
      .finally(() => { consumeInFlight = null; });
    return consumeInFlight;
  }

  return { consumeAndShow, show };
})();

export { StarterReward };
